import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Typography,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
} from "@mui/material";
import {
  MonitorHeart as BowelIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { useQueryClient, useQuery, useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { QK } from "../../../../../../base/qk";
import { useSnackbar } from "notistack";
import {
  getSecAssessmentByClientID,
  createSecAssessment,
  deleteSecAssessmentID,
} from "../../../../../../api/services/observations";
import CustomTextArea from "../../../../../CustomForm/CustomTextArea";
import CustomCheckbox from "../../../../../CustomForm/CustomCheckbox";
import { useUserProfile } from "../../../../../../hooks/userProfile";
import DataTable from "../../../../../Table/DataTable";
import ConfirmModal from "../../../../../ConfirmModal/ConfirmModal";
import CustomDateField from "../../../../../FormFields/CustomDateField";
import EditBowelMovementModal from "./editBowelMovement";
import { bowelMovement } from "./bowelMovement.config";

const BowelMovement = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    type: "",
    notes: "",
    date_time: "",
    handover: false,
    manager: false,
  });
  
  const queryClient = useQueryClient();
  const { id: clientID } = useParams();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [rowID, setRowID] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [showModal, setShowModal] = useState(false);
  const [selectedID, setSelectedID] = useState(null);

  const profile = useUserProfile();
  const assessmentType = "bowel_movement";

  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleCheckBox = useCallback((isChecked, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: isChecked,
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      type: "",
      notes: "",
      date_time: "",
      handover: false,
      manager: false,
    });
  }, []);

  const { data: assessments, isLoading } = useQuery(
    [QK.bowelMovements, clientID],
    () => getSecAssessmentByClientID(clientID, assessmentType),
    {
      enabled: !!clientID,
    }
  );

  const createMutation = useMutation(createSecAssessment, {
    onSuccess: () => {
      enqueueSnackbar("Bowel movement record created successfully.", {
        variant: "success",
      });
      queryClient.invalidateQueries([QK.bowelMovements, clientID]);
      resetForm();
    },
    onError: (error) => {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to create bowel movement record.",
        {
          variant: "error",
        }
      );
    },
  });

  const deleteMutation = useMutation(deleteSecAssessmentID, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.bowelMovements, clientID]);
      enqueueSnackbar("Bowel movement record deleted successfully", { 
        variant: "success" 
      });
      setShowConfirmModal(false);
    },
    onError: () => {
      enqueueSnackbar("Failed to delete bowel movement record", { 
        variant: "error" 
      });
    },
  });

  const validateForm = useCallback((data) => {
    const newErrors = {};
    
    if (!data.type) {
      newErrors.type = "Stool type is required";
    }
    if (!data.date_time) {
      newErrors.date_time = "Date and time is required";
    }

    return Object.keys(newErrors).length === 0;
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (validateForm(formData)) {
        const updatedFormData = {
          ...formData,
          client: clientID,
          carer: profile?.id,
          assessment_type: assessmentType,
        };
        createMutation.mutate(updatedFormData);
      }
    },
    [formData, clientID, profile, createMutation, validateForm]
  );

  const handleOpenModal = useCallback((id) => {
    setSelectedID(id);
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setSelectedID(null);
  }, []);

  const handleUpdate = useCallback((id) => {
    handleOpenModal(id);
  }, [handleOpenModal]);

  const handleDelete = useCallback((ID) => {
    setRowID(ID);
    setShowConfirmModal(true);
  }, []);

  const handleConfirm = useCallback(() => {
    if (rowID) {
      deleteMutation.mutate(rowID);
    }
  }, [rowID, deleteMutation]);

  const headers = useMemo(
    () => [
      { key: "type", label: "Stool Type" },
      { key: "notes", label: "Notes" },
      { key: "date_time", label: "Date & Time" },
      { key: "manager", label: "Manager" },
      { key: "handover", label: "Handover" },
    ],
    []
  );

  const renderCell = useCallback(
    (item, header) => {
      if (header.key === "type") {
        const bowelType = bowelMovement.find(b => b.type === item.type);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {bowelType && (
              <img 
                src={bowelType.image} 
                alt={bowelType.type} 
                style={{ 
                  width: 50, 
                  height: 50, 
                  objectFit: "contain",
                  borderRadius: "8px",
                  backgroundColor: "#F8FAFC",
                  padding: "4px"
                }}
              />
            )}
            <Typography variant="body2" fontWeight={500}>
              {item.type || "-"}
            </Typography>
          </Box>
        );
      }
      if (header.key === "date_time") {
        return item.date_time ? new Date(item.date_time).toLocaleString() : "-";
      }
      if (header.key === "notes") {
        return (
          <span
            style={{
              display: "block",
              maxWidth: 150,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item.notes || "-"}
          </span>
        );
      }
      if (header.key === "manager") {
        return item.manager ? (
          <Chip
            label="Yes"
            size="small"
            sx={{
              height: 20,
              fontSize: "0.6rem",
              fontWeight: 500,
              bgcolor: "#D1FAE5",
              color: "#039855",
            }}
          />
        ) : (
          <Chip
            label="No"
            size="small"
            sx={{
              height: 20,
              fontSize: "0.6rem",
              fontWeight: 500,
              bgcolor: "#FEE2E2",
              color: "#DC2626",
            }}
          />
        );
      }
      if (header.key === "handover") {
        return item.handover ? (
          <Chip
            label="Yes"
            size="small"
            sx={{
              height: 20,
              fontSize: "0.6rem",
              fontWeight: 500,
              bgcolor: "#D1FAE5",
              color: "#039855",
            }}
          />
        ) : (
          <Chip
            label="No"
            size="small"
            sx={{
              height: 20,
              fontSize: "0.6rem",
              fontWeight: 500,
              bgcolor: "#FEE2E2",
              color: "#DC2626",
            }}
          />
        );
      }
      return item[header.key] || "-";
    },
    []
  );

  const isSubmitting = createMutation.isLoading;

  return (
    <Box>
      {/* Form Card */}
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
          mb: 3,
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#FAFBFC",
            borderBottom: "1px solid #F2F4F7",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: alpha(theme.palette.success.main, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BowelIcon sx={{ color: theme.palette.success.main, fontSize: 18 }} />
            </Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: "#1A1A2E" }}>
              Record Bowel Movement
            </Typography>
            <Chip
              label="New"
              size="small"
              sx={{
                height: 20,
                fontSize: "0.6rem",
                fontWeight: 500,
                bgcolor: "#D1FAE5",
                color: "#039855",
              }}
            />
          </Box>
        </Box>

        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleSubmit} id="bowel-form">
            <Grid container spacing={3}>
              {/* Stool Type Selection */}
              <Grid item xs={12}>
                <Paper
                  sx={{
                    p: 3,
                    bgcolor: "#F8FAFC",
                    borderRadius: "12px",
                    border: "1px solid #E2E8F0",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 3, color: "#1A1A2E" }}>
                    Select Stool Type *
                  </Typography>
                  <RadioGroup
                    name="type"
                    value={formData.type || ""}
                    onChange={handleChange}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    {bowelMovement.map((item) => (
                      <FormControlLabel
                        key={item.type}
                        value={item.type}
                        control={<Radio />}
                        label={
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                            <img 
                              src={item.image} 
                              alt={item.type} 
                              style={{ 
                                width: 80, 
                                height: 80, 
                                objectFit: "contain",
                                borderRadius: "12px",
                                backgroundColor: "white",
                                padding: "8px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                              }}
                            />
                            <Box sx={{ textAlign: "center" }}>
                              <Typography variant="body1" fontWeight={600} color="#1A1A2E">
                                {item.type}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ display: "block" }}>
                                {item.title}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        sx={{
                          flex: "1 0 auto",
                          minWidth: "140px",
                          maxWidth: "180px",
                          p: 2,
                          borderRadius: "12px",
                          border: "2px solid #E2E8F0",
                          bgcolor: "white",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: "#F1F5F9",
                            borderColor: "#94A3B8",
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                          },
                          "&.Mui-checked": {
                            borderColor: theme.palette.success.main,
                            bgcolor: alpha(theme.palette.success.main, 0.04),
                            boxShadow: `0 0 0 4px ${alpha(theme.palette.success.main, 0.1)}`,
                          },
                        }}
                      />
                    ))}
                  </RadioGroup>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomDateField
                  name="date_time"
                  label="Date & Time *"
                  value={formData.date_time || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <CustomTextArea
                  label="Notes"
                  name="notes"
                  value={formData.notes || ""}
                  onChange={handleChange}
                  rows={2}
                  fullWidth
                  placeholder="Add any additional notes..."
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  <CustomCheckbox
                    label="Share with Manager"
                    name="manager"
                    checked={formData.manager || false}
                    onClick={handleCheckBox}
                  />
                  <CustomCheckbox
                    label="Handover"
                    name="handover"
                    checked={formData.handover || false}
                    onClick={handleCheckBox}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={isSubmitting}
                    startIcon={<ClearIcon />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 500,
                      borderRadius: "8px",
                      borderColor: "#D0D5DD",
                      color: "#667085",
                      "&:hover": { bgcolor: "#F9FAFB", borderColor: "#98A2B3" },
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={18} />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "8px",
                      bgcolor: "#059669",
                      px: 4,
                      "&:hover": {
                        bgcolor: "#047857",
                        boxShadow: "0px 4px 12px rgba(5, 150, 105, 0.3)",
                      },
                    }}
                  >
                    {isSubmitting ? "Saving..." : "Save Record"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Table Card */}
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#FAFBFC",
            borderBottom: "1px solid #F2F4F7",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: "#1A1A2E" }}>
              Bowel Movement Records
            </Typography>
            <Chip
              label={assessments?.length || 0}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.6rem",
                fontWeight: 500,
                bgcolor: "#F2F4F7",
                color: "#475467",
              }}
            />
          </Box>
        </Box>

        <CardContent sx={{ p: 0 }}>
          <DataTable
            data={assessments || []}
            headers={headers}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            canUpdate={true}
            canDelete={true}
            renderCell={renderCell}
            loading={isLoading}
          />
        </CardContent>
      </Card>

      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={handleConfirm}
        title="Delete Confirmation"
        body="Are you sure you want to delete this bowel movement record?"
        isDelete={true}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <EditBowelMovementModal
        show={showModal}
        handleClose={handleCloseModal}
        selectedID={selectedID}
      />
    </Box>
  );
};

export default BowelMovement;